import { FILTER_RELATIVE_DATE_RANGE, FilterTypeEnum } from '@creditsights/api-ui-web';
import {
  Button,
  ButtonMode,
  ButtonSize,
  DateRange,
  Filter as FilterComponent,
  ICONS,
  Icon,
  TreeNodeModel,
  getDateRange,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { add, format, isAfter, isBefore, isEqual, isValid } from 'date-fns';
import { cloneDeep, concat, isEqual as deepEqual, has } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { DateType } from '../../Base.constants';
import { useLocalStorage, useWindowResizeListener } from '../../hooks/common';
import useFetchFilters from '../../hooks/tags/useFetchFilters';
import {
  CSViewOption,
  ContentTypeFilter,
  ContentTypeTag,
  DateFilterOption,
  FILTER_TYPE_LABELS_PLURAL,
  FILTER_TYPE_LABELS_SINGULAR,
  Filter,
  FilterOption,
  FilterType,
  Filters,
  IssuerOption,
  KeywordFilterOption,
} from '../../models';
import { SEARCH_TERM_URL_KEY } from '../../pages/SearchResults/SearchResults.constants';
import { DATE_FORMATS, parseDate } from '../../utils/dateUtils';
import { convertToTreeModel, filterNewsTags } from '../../utils/filterUtils';
import { useFiltersContext } from '../Filters';
import {
  CONTENT_TYPE_FILTER_URL_KEY,
  DATE_RELATIVE_RANGE_URL_KEY,
  END_DATE_URL_KEY,
  KEYWORD_URL_KEY,
  START_DATE_URL_KEY,
  TITLE_CONTAINS_URL_KEY,
  convertFiltersToMap,
  findTitleContains,
  getApiFilterTypes,
  getHiddenCheckedKeys,
  transformTags,
} from './FiltersBar.logic';
import styles from './FiltersBar.module.scss';
import KeywordFilter from './KeywordFilter';
import MoreButton from './MoreButton';
import { IsFiltersBarExpandedKey, NEWS, changeFilterName, changeFilterNames } from './constants';

interface FiltersBarProps {
  onChange?: (filters: Filters) => void;
  filterTypes: FilterOption[];
  hiddenFilterTypes?: FilterOption[];
  articleType?: ContentTypeFilter;
  transformFilter?: (item: Filter, articleType?: ContentTypeFilter) => Filter;
  disableMultiline?: boolean;
  contentTypeFiltersMap?: Record<ContentTypeFilter, ContentTypeTag[]>;
  translate?: boolean;
}

export interface FiltersBarHandle {
  reset: () => void;
}

export interface DateRangeChangedEventProps {
  startDate?: DateType;
  endDate?: DateType;
  relativeRange?: FILTER_RELATIVE_DATE_RANGE | null;
}

const cx = classNames.bind(styles);

// height values in pixels
// TODO: refactor error height with dynamic value controlled in state
const ERROR_HEIGHT = 24;
const COLLAPSED_WRAPPER_HEIGHT = 24;

const FiltersBar = (
  {
    onChange,
    filterTypes,
    articleType,
    transformFilter,
    hiddenFilterTypes = [],
    disableMultiline,
    contentTypeFiltersMap,
    translate,
  }: FiltersBarProps,
  ref
): React.ReactElement | null => {
  const apiFilterTypes = getApiFilterTypes(filterTypes);
  const { data: fetchedFilters = [], isLoading } = useFetchFilters(apiFilterTypes);
  const feedFilters = useMemo(
    () =>
      fetchedFilters.map((filter) => {
        const transformedFilter = transformFilter ? transformFilter(filter, articleType) : filter;

        return {
          type: transformedFilter.type,
          options:
            transformedFilter.type === FilterTypeEnum.news_source
              ? convertToTreeModel({
                  options: [{ tagId: NEWS, displayName: '', children: transformedFilter.options }],
                })[0].children!
              : convertToTreeModel({ options: transformedFilter.options }),
        };
      }),
    [fetchedFilters, articleType]
  );
  const { setFilters } = useFiltersContext();

  const [selectedTags, setSelectedTags] = useState<Record<string, string[]>>({});
  const [selectedStartDate, setSelectedStartDate] = useState<DateType>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<DateType>(null);
  const [selectedRelativeRange, setSelectedRelativeRange] = useState<FILTER_RELATIVE_DATE_RANGE | null>(null);
  const [selectedKeyword, setSelectedKeyword] = useState<string>('');
  const [isMultiline, setIsMultiline] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>(IsFiltersBarExpandedKey, true);
  const [filtersWrapperElement, setFiltersWrapperElement] = useState<HTMLDivElement | null>(null);
  const [filtersArray, setFiltersArray] = useState<HTMLDivElement[]>([]);
  const [hiddenCheckedKeysCount, setHiddenCheckedKeysCount] = useState<number>(0);
  const [openedFilter, setOpenedFilter] = useState<FilterType | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<boolean>(false);
  const [currentFilterUrlParams, setCurrentFilterUrlParams] = useState<[string, string][]>();
  const [previousSearchTerm, setPreviousSearchTerm] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      reset: handleReset,
    }),
    []
  );

  // Used only for date-range filter component to reset input value
  const [resetDateInput, setResetDateInput] = useState(0);
  const resetDate = () => setResetDateInput((prevState) => prevState + 1);

  const filterUrlKeys = [
    ...changeFilterNames(apiFilterTypes, articleType),
    ...hiddenFilterTypes,
    DATE_RELATIVE_RANGE_URL_KEY,
    START_DATE_URL_KEY,
    END_DATE_URL_KEY,
    TITLE_CONTAINS_URL_KEY,
    KEYWORD_URL_KEY,
    CONTENT_TYPE_FILTER_URL_KEY,
  ];

  if (articleType === ContentTypeFilter.ALL) {
    filterUrlKeys.push(FilterTypeEnum.news_source);
    filterUrlKeys.push(FilterTypeEnum.covenants_source);
  }

  const filtersWrapperElementState = useRef(filtersWrapperElement);
  const setFiltersWrapperElementState = (data) => {
    filtersWrapperElementState.current = data;
    setFiltersWrapperElement(data);
  };

  const filtersArrayState = useRef(filtersArray);
  const setFiltersArrayState = (data) => {
    filtersArrayState.current = data;
    setFiltersArray(data);
  };

  const selectedTagsState = useRef(selectedTags);
  const setSelectedTagsState = (data: Record<string, string[]>) => {
    selectedTagsState.current = data;
    setSelectedTags(data);
  };

  const selectedStartDateState = useRef(selectedStartDate);
  const setSelectedStartDateState = (data) => {
    selectedStartDateState.current = data;
    setSelectedStartDate(data);
  };

  const selectedEndDateState = useRef(selectedEndDate);
  const setSelectedEndDateState = (data) => {
    selectedEndDateState.current = data;
    setSelectedEndDate(data);
  };

  const errorState = useRef(error);
  const setErrorState = (data: boolean) => {
    errorState.current = data;
    setError(data);
  };

  const isExpandedState = useRef(isExpanded);
  const setIsExpandedState = (isExpanded: boolean) => {
    isExpandedState.current = isExpanded;
    setIsExpanded(isExpanded);
  };

  const formatDate = (date: DateType) => {
    return date ? format(date, DATE_FORMATS.YEAR_MONTH_DAY) : undefined;
  };

  const combineSourceTags = (tags: Record<string, string[]>) => {
    if (articleType !== ContentTypeFilter.ALL) {
      return tags;
    }

    if (has(tags, FilterTypeEnum.covenants_source.toString()) || has(tags, FilterTypeEnum.news_source.toString())) {
      const copiedTags = cloneDeep(tags);

      const groupedSourceTags: string[] = concat(
        tags[FilterTypeEnum.source] || [],
        tags[FilterTypeEnum.covenants_source] || [],
        tags[FilterTypeEnum.news_source] || []
      );

      delete copiedTags[FilterType.source];
      delete copiedTags[FilterType.covenants_source];
      delete copiedTags[FilterType.news_source];

      copiedTags[FilterType.source] = groupedSourceTags;

      return copiedTags;
    }

    return tags;
  };

  const getAllSelectedFilters = (
    oldTags: Record<string, string[]>,
    startDate: DateType,
    endDate: DateType,
    relativeRange: FILTER_RELATIVE_DATE_RANGE | null,
    keyword: string | null,
    contentType: string | null
  ): Filters => {
    const isRangeValid = startDate && endDate ? isBefore(startDate, endDate) || isEqual(startDate, endDate) : true;

    let adjustedEndDate: Date | undefined;
    if (endDate && isValid(endDate)) {
      adjustedEndDate = relativeRange ? endDate : add(endDate, { hours: 23, minutes: 59, seconds: 59 });
    }
    const tags = combineSourceTags(oldTags);
    const titleContains = findTitleContains(tags);
    const transformedTags = transformTags(tags);
    const contentTypeFilters =
      contentType && contentTypeFiltersMap && contentTypeFiltersMap[contentType]?.length > 0
        ? contentTypeFiltersMap[contentType]
        : undefined;

    return {
      tags: contentTypeFilters
        ? {
            ...transformedTags,
            [CONTENT_TYPE_FILTER_URL_KEY]: contentTypeFilters,
          }
        : transformedTags,
      startDate: isRangeValid && startDate ? startDate : undefined,
      endDate: isRangeValid ? adjustedEndDate : undefined,
      titleContains: titleContains || undefined,
      relativeRange: relativeRange || undefined,
      keyword: keyword || undefined,
    };
  };

  const filtersWrapperRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      setFiltersWrapperElementState(node);
      setIsMultiline(!disableMultiline && node.scrollHeight > COLLAPSED_WRAPPER_HEIGHT);
    }
  }, []);

  const filterRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      setFiltersArrayState([...filtersArrayState.current, node]);
    }
  }, []);

  const checkIfMultiline = () => {
    const filtersWrapperElement = filtersWrapperElementState.current;
    const filtersArray = filtersArrayState.current;
    const selectedTags = selectedTagsState.current;
    const selectedStartDate = selectedStartDateState.current;
    const selectedEndDate = selectedEndDateState.current;
    const error = errorState.current;
    const isExpanded = isExpandedState.current;

    if (!filtersWrapperElement) {
      return;
    }

    // collapsed filters wrapper becomes taller, when error is displayed
    const multiline = error
      ? filtersWrapperElement.scrollHeight > COLLAPSED_WRAPPER_HEIGHT + ERROR_HEIGHT
      : filtersWrapperElement.scrollHeight > COLLAPSED_WRAPPER_HEIGHT;

    setIsMultiline(!disableMultiline && multiline);
    if (multiline && !isExpanded) {
      const isDateSelected = !!selectedStartDate || !!selectedEndDate;
      const count = getHiddenCheckedKeys(isDateSelected, filtersArray, filtersWrapperElement, selectedTags);
      setHiddenCheckedKeysCount(count);
    }
  };

  useWindowResizeListener(() => {
    if (!isMobile) {
      setOpenedFilter(null);
    }
    checkIfMultiline();
  });

  useEffect(() => {
    checkIfMultiline();
  }, [filtersWrapperElement, feedFilters, error, selectedTags]);

  const getDateParam = (searchParameter: string): DateType => {
    const dateParam = searchParams.get(searchParameter);
    if (dateParam) {
      const date = parseDate(dateParam, DATE_FORMATS.YEAR_MONTH_DAY);
      return isValid(date) ? date : null;
    }
    return null;
  };

  useEffect(() => {
    const filterUrlParams = Array.from(searchParams.entries()).filter((param) => filterUrlKeys.includes(param[0]));

    const currentSearchTerm = searchParams.get(SEARCH_TERM_URL_KEY);
    // reset custom dates when searchTerm changes
    if (currentSearchTerm !== previousSearchTerm) {
      resetDate();
      setPreviousSearchTerm(currentSearchTerm);
    }

    const filters = convertFiltersToMap(filterUrlParams);
    const renamedFilters = combineSourceTags(filters);
    setSelectedTagsState(renamedFilters);

    if (deepEqual(filterUrlParams, currentFilterUrlParams)) {
      return;
    }

    setCurrentFilterUrlParams(filterUrlParams);

    const keyword = searchParams.get(KEYWORD_URL_KEY);
    const contentType = searchParams.get(CONTENT_TYPE_FILTER_URL_KEY);
    let startDate: DateType;
    let endDate: DateType;
    let relativeRange: FILTER_RELATIVE_DATE_RANGE | null;
    const rangeParam = searchParams.get(DATE_RELATIVE_RANGE_URL_KEY);
    const rangeEnum = rangeParam ? FILTER_RELATIVE_DATE_RANGE[rangeParam] : null;
    if (rangeEnum) {
      const range = getDateRange(rangeEnum);
      startDate = range.startDate;
      endDate = range.endDate;
      relativeRange = rangeEnum;
    } else {
      startDate = getDateParam(START_DATE_URL_KEY);
      endDate = getDateParam(END_DATE_URL_KEY);
      relativeRange = null;
      if (startDate && endDate && isAfter(startDate, endDate)) {
        startDate = null;
        endDate = null;
      }
    }

    setSelectedRelativeRange(relativeRange);
    setSelectedStartDateState(startDate);
    setSelectedEndDateState(endDate);

    if (keyword) {
      setSelectedKeyword(keyword);
    }

    const selectedFilters = getAllSelectedFilters(filters, startDate, endDate, relativeRange, keyword, contentType);
    setFilters(selectedFilters);
    if (onChange) {
      onChange(selectedFilters);
    }
  }, [searchParams]);

  const handleToggle = () => {
    if (!filtersWrapperElement) {
      return;
    }
    setIsExpandedState(!isExpanded);
    if (isExpanded) {
      setTimeout(() => {
        const isDateSelected = !!selectedStartDate || !!selectedEndDate;
        const hiddenSelectedItems = getHiddenCheckedKeys(
          isDateSelected,
          filtersArray,
          filtersWrapperElement,
          selectedTags
        );
        setHiddenCheckedKeysCount(hiddenSelectedItems);
      }, 100);
    }
    setHiddenCheckedKeysCount(0);
  };

  const resetContentTypeFilters = (params: [string, string][]): [string, string][] => {
    return params.filter(
      (param) => ![FilterTypeEnum.news_source.toString(), FilterTypeEnum.covenants_source.toString()].includes(param[0])
    );
  };

  const handleReset = () => {
    const params = Array.from(searchParams.entries()).filter(
      (param) => !filterUrlKeys.includes(param[0]) || param[0] === CONTENT_TYPE_FILTER_URL_KEY
    );
    const clearedParams = resetContentTypeFilters(params);
    setSearchParams(clearedParams);
    setHiddenCheckedKeysCount(0);
    resetDate();
    setSelectedKeyword('');
  };

  const setFilterParams = (type: FilterType, options: TreeNodeModel[], ids: string[]) => {
    const newSearchParams = createSearchParams(searchParams);
    newSearchParams.delete(type);
    if (type === FilterType.source && articleType === ContentTypeFilter.ALL) {
      newSearchParams.delete(FilterType.covenants_source);
      newSearchParams.delete(FilterType.news_source);
    }
    newSearchParams.delete(TITLE_CONTAINS_URL_KEY);
    if (type === FilterType.source) {
      ids.filter(filterNewsTags).forEach((id) => {
        const newType = options.find((option) => {
          if (!option.children) {
            return false;
          }
          return option.children?.some((item) => item.key === id || item.children?.some((child) => child.key === id));
        });
        if (newType?.title === ContentTypeFilter.COVENANTS) {
          newSearchParams.append(FilterType.covenants_source, id);
        } else if (newType?.title === ContentTypeFilter.NEWS) {
          newSearchParams.append(FilterType.news_source, id);
        } else {
          newSearchParams.append(type, id);
        }
      });
    } else {
      ids.filter(filterNewsTags).forEach((id) => newSearchParams.append(type, id));
    }
    setSearchParams(newSearchParams);
  };

  const handleDateChange = ({ startDate, endDate, relativeRange }: DateRangeChangedEventProps) => {
    const newSearchParams = createSearchParams(searchParams);
    if (relativeRange) {
      newSearchParams.set(DATE_RELATIVE_RANGE_URL_KEY, relativeRange.toString());
      newSearchParams.delete(START_DATE_URL_KEY);
      newSearchParams.delete(END_DATE_URL_KEY);
    } else {
      newSearchParams.delete(START_DATE_URL_KEY);
      newSearchParams.delete(END_DATE_URL_KEY);
      if (startDate && isValid(startDate)) {
        const formattedStartDate = formatDate(startDate);
        if (formattedStartDate) {
          newSearchParams.set(START_DATE_URL_KEY, formattedStartDate);
        }
      }
      if (endDate && isValid(endDate)) {
        const formattedEndDate = formatDate(endDate);
        if (formattedEndDate) {
          newSearchParams.set(END_DATE_URL_KEY, formattedEndDate);
        }
      }
      newSearchParams.delete(DATE_RELATIVE_RANGE_URL_KEY);
    }
    setSearchParams(newSearchParams);
  };

  const handleKeywordKeyDown = (event) => {
    if (event.key === 'Enter') {
      const newSearchParams = createSearchParams(searchParams);
      if (selectedKeyword?.trim()) {
        newSearchParams.set(KEYWORD_URL_KEY, selectedKeyword);
      } else {
        newSearchParams.delete(KEYWORD_URL_KEY);
      }
      setSearchParams(newSearchParams);
    } else if (event.key === 'Escape') {
      event.target.blur();
    }
  };
  const handleKeywordClear = () => {
    setSelectedKeyword('');
    const newSearchParams = createSearchParams(searchParams);
    newSearchParams.delete(KEYWORD_URL_KEY);
    setSearchParams(newSearchParams);
  };

  const renderFilterOption = (filterType: FilterOption) => {
    if (filterType === CSViewOption || filterType === IssuerOption) {
      return null;
    }
    if (filterType === DateFilterOption) {
      return (
        <DateRange
          key={filterType}
          startDate={selectedStartDate}
          endDate={selectedEndDate}
          relativeRange={selectedRelativeRange}
          onChange={handleDateChange}
          resetInput={resetDateInput}
          error={error}
          setError={setErrorState}
          popupClassName={translate ? 'to-translate' : undefined}
        />
      );
    }
    if (filterType === KeywordFilterOption) {
      return (
        <KeywordFilter
          key={filterType}
          value={selectedKeyword}
          placeholder="Search by keyword"
          maxLength={200}
          onKeyDown={handleKeywordKeyDown}
          onChange={(event) => setSelectedKeyword(event.target.value)}
          onClear={handleKeywordClear}
          searchDataTestId="filters-search-icon"
          inputDataTestId="keyword-input"
          clearDataTestId="keyword-clear-button"
          autoComplete="off"
        />
      );
    }
    const renamedFilter = changeFilterName(filterType, articleType);
    const filter = feedFilters.find((feedFilter) => feedFilter.type === renamedFilter);
    if (!filter) {
      return null;
    }

    return (
      <div className={cx('filter-wrapper')} data-filter-name={filter.type} key={renamedFilter} ref={filterRef}>
        <FilterComponent
          data={filter.options}
          placeholder={
            selectedTags[filter.type]?.length > 1
              ? FILTER_TYPE_LABELS_PLURAL[filter.type]
              : FILTER_TYPE_LABELS_SINGULAR[filter.type]
          }
          dataTestId={`filter-${filter.type}`}
          onChange={(ids) => setFilterParams(filter.type, filter.options, ids)}
          checkedKeys={selectedTags[filter.type] || []}
          open={openedFilter === filter.type}
          onOpen={() => setOpenedFilter(filter.type)}
          onClose={() => setOpenedFilter(null)}
          key={renamedFilter}
          popupClassName={translate ? 'to-translate' : undefined}
        />
      </div>
    );
  };

  if (isLoading) {
    return null;
  }
  return (
    <div className={cx('body', { 'is-multiline': isMultiline })}>
      <div
        className={classNames(
          cx('filters', {
            'is-multiline': isMultiline,
            'is-collapsed': !isExpanded,
            'is-singleline': disableMultiline,
          }),
          { 'to-translate': translate }
        )}
        ref={filtersWrapperRef}
        data-testid="filters-wrapper"
      >
        {filterTypes.map((filterType) => renderFilterOption(filterType))}
      </div>
      <div className={cx('tools')} data-testid="feed-options-tools">
        {isMultiline && (
          <MoreButton
            handleToggle={handleToggle}
            hiddenCheckedKeysCount={hiddenCheckedKeysCount}
            isExpanded={isExpanded}
          />
        )}
        <Button
          dataTestId="filters-reset-button"
          mode={ButtonMode.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={handleReset}
        >
          <Icon name={ICONS.Clear} width={12} height={12} />
          Reset
        </Button>
      </div>
    </div>
  );
};

export default forwardRef<FiltersBarHandle, FiltersBarProps>(FiltersBar);
